import type { ErrorSchema } from '@rjsf/utils';
import type { JSONSchema7 } from 'json-schema';
import type { z } from 'zod';

/**
 * Normalize form data for Zod validation by converting `undefined` values
 * to empty strings for required string fields only.
 *
 * RJSF treats empty text inputs as `undefined` instead of `""`, which causes
 * Zod's type validation to fail before reaching schema validation rules like
 * `.min(1)`. This results in generic type errors ("Expected string, received
 * undefined") instead of custom validation messages.
 *
 * This function uses the JSON Schema to identify required string fields and
 * normalizes `undefined` → `""` only for those fields. Optional fields remain
 * `undefined` to allow validation to pass.
 *
 * @param data - The form data to normalize
 * @param schema - The JSON Schema describing the data structure
 * @returns Normalized data with undefined required string fields converted to empty strings
 *
 * @example
 * ```typescript
 * const data = { firstName: undefined, middleName: undefined, age: 25 };
 * const schema = {
 *   type: 'object',
 *   properties: {
 *     firstName: { type: 'string' },
 *     middleName: { type: 'string' },
 *     age: { type: 'number' }
 *   },
 *   required: ['firstName'] // middleName is optional
 * };
 * const normalized = normalizeForValidation(data, schema);
 * // Result: { firstName: '', middleName: undefined, age: 25 }
 * ```
 */
export const normalizeForValidation = <T = unknown>(
  data: T,
  schema: JSONSchema7,
): T => {
  if (data === null || data === undefined) {
    return data;
  }

  // Recursively normalize based on schema type
  const normalizeValue = (
    value: unknown,
    currentSchema: JSONSchema7,
    fieldName?: string,
    parentSchema?: JSONSchema7,
  ): unknown => {
    // Handle null/undefined values
    if (value === null || currentSchema.type === 'null') {
      return value;
    }

    // Handle `anyOf` / `oneOf` / `allOf`, which are common in nullable schemas
    // For nullable objects, we want to use the object schema for normalization
    if (currentSchema.anyOf || currentSchema.oneOf || currentSchema.allOf) {
      const schemaOptions =
        currentSchema.anyOf || currentSchema.oneOf || currentSchema.allOf;
      if (schemaOptions && Array.isArray(schemaOptions)) {
        // Find the first non-null schema (typically the object schema)
        // Note: JSONSchema7Definition can be a boolean, so we need a type guard
        const objectSchema = schemaOptions.find(
          (schemaItem): schemaItem is JSONSchema7 =>
            typeof schemaItem === 'object' &&
            schemaItem !== null &&
            schemaItem.type !== 'null',
        );
        if (objectSchema) {
          return normalizeValue(value, objectSchema, fieldName, parentSchema);
        }
      }
    }

    // For string fields, convert undefined to empty string ONLY if the field
    // is required. Check the parent schema's required array.
    if (currentSchema.type === 'string' && value === undefined) {
      // If we have a field name and parent schema, check if it's required
      if (fieldName && parentSchema?.required) {
        const isRequired = parentSchema.required.includes(fieldName);
        // Only normalize required fields
        return isRequired ? '' : undefined;
      }
      // If no parent context, assume it's required (for root-level strings)
      return '';
    }

    if (currentSchema.type === 'string') {
      return value;
    }

    // For arrays, normalize each item
    if (currentSchema.type === 'array' && Array.isArray(value)) {
      const itemSchema = currentSchema.items as JSONSchema7 | undefined;
      if (!itemSchema) return value;

      return value.map(item => normalizeValue(item, itemSchema));
    }

    // For objects, normalize each property
    if (currentSchema.type === 'object' && typeof value === 'object') {
      const normalized: Record<string, unknown> = {};
      const properties = currentSchema.properties || {};

      // Process all properties defined in the schema, passing parent context
      Object.keys(properties).forEach(key => {
        const propertySchema = properties[key] as JSONSchema7;
        const propertyValue = (value as Record<string, unknown>)[key];
        normalized[key] = normalizeValue(
          propertyValue,
          propertySchema,
          key,
          currentSchema,
        );
      });

      // Include any additional properties not in schema (pass through as-is)
      Object.keys(value as Record<string, unknown>).forEach(key => {
        if (!(key in normalized)) {
          normalized[key] = (value as Record<string, unknown>)[key];
        }
      });

      return normalized;
    }

    // For all other types (number, boolean, etc.), return as-is
    return value;
  };

  return normalizeValue(data, schema) as T;
};

/**
 * Process Zod validation errors into RJSF error schema format.
 *
 * This function transforms Zod validation errors into a format that RJSF can
 * understand and display inline with form fields. It maintains a mapping of
 * error paths to support live validation where only previously errored fields
 * are re-validated as the user types.
 *
 * The function builds an error schema tree that matches the form structure,
 * allowing RJSF to display errors next to the appropriate fields. It also
 * generates user-friendly error messages for display at the form level.
 *
 * @param error - The ZodError object containing validation issues
 * @param previousErrorPaths - Optional set of previously errored field paths
 * for selective re-validation
 * @returns Object containing:
 *   - `errorSchema`: RJSF-compatible error structure for inline display
 *   - `errorPaths`: Set of field paths that have errors
 *   - `errorMessages`: Array of formatted error messages
 */
export const processZodErrors = (
  error: z.ZodError,
  previousErrorPaths?: Set<string>,
): {
  errorSchema: ErrorSchema;
  errorPaths: Set<string>;
  errorMessages: string[];
} => {
  const errorSchema: ErrorSchema = {};
  const errorPaths = new Set<string>();
  const errorMessages: string[] = [];

  error.issues.forEach(issue => {
    const pathStr = issue.path.join('.');

    // If we have previous error paths, only include errors that were previously tracked
    if (!previousErrorPaths || previousErrorPaths.has(pathStr)) {
      errorPaths.add(pathStr);

      // Build error schema for RJSF
      let current = errorSchema;
      issue.path.forEach((segment, index) => {
        const segmentStr = String(segment);
        const isLastSegment = index === issue.path.length - 1;

        if (isLastSegment) {
          if (!current[segmentStr]) {
            current[segmentStr] = { __errors: [] } as unknown as ErrorSchema;
          }
          const fieldError = current[segmentStr] as ErrorSchema & {
            __errors?: string[];
          };
          // eslint-disable-next-line no-underscore-dangle
          if (!fieldError.__errors) {
            // eslint-disable-next-line no-underscore-dangle
            fieldError.__errors = [];
          }
          // eslint-disable-next-line no-underscore-dangle
          fieldError.__errors.push(issue.message);
        } else {
          if (!current[segmentStr]) {
            current[segmentStr] = {};
          }

          // Handle array fields
          const nextSegment = issue.path[index + 1];
          if (typeof nextSegment === 'number') {
            if (!current[segmentStr][nextSegment]) {
              current[segmentStr][nextSegment] = {};
            }
            current = current[segmentStr];
          } else {
            current = current[segmentStr];
          }
        }
      });

      // Add to error messages
      const message = pathStr ? `${pathStr}: ${issue.message}` : issue.message;
      if (!previousErrorPaths || previousErrorPaths.size === 0) {
        errorMessages.push(message);
      }
    }
  });

  return { errorSchema, errorPaths, errorMessages };
};
